import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { locale, addLocale } from 'primereact/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './ReporteRH.css';

// Configuración de localización en español para PrimeReact
addLocale('es', {
  firstDayOfWeek: 1,
  dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
  monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
  monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  today: 'Hoy',
  clear: 'Limpiar',
  weekHeader: 'Sm',
  dateFormat: 'dd/mm/yy',
  emptyMessage: 'No se encontraron registros',
  filterPlaceholder: 'Buscar',
  choose: 'Elegir',
  upload: 'Subir',
  cancel: 'Cancelar',
});

locale('es');

const ReportesRH = () => {
  const [view, setView] = useState('menu');
  const [puntosData, setPuntosData] = useState([]);
  const [canjeData, setCanjeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);

  const username = localStorage.getItem('username');

  useEffect(() => {
    if (view === 'puntos') {
      fetchPuntos();
    } else if (view === 'canje') {
      fetchCanje();
    }
  }, [view]);

  const fetchPuntos = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/puntuacion/findall');
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.id,
        nombre: item.empleado.nombre,
        puntosPositivos: item.categoria.puntos > 0 ? item.categoria.puntos : 0,
        puntosNegativos: item.categoria.puntos < 0 ? item.categoria.puntos : 0,
        fecha: new Date(item.fecha),
      }));
      setPuntosData(formattedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching puntos:', error);
      setLoading(false);
    }
  };

  const fetchCanje = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/puntuacion/canjepremio/findall');
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.id,
        nombre: item.empleado.nombre,
        premio: item.premio.nombre,
        descripcionPremio: item.premio.descripcion,
        puntos: item.premio.puntos,
        fecha: new Date(item.fecha),
      }));
      setCanjeData(formattedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching canje:', error);
      setLoading(false);
    }
  };

  const handleBackToMenu = () => {
    setView('menu');
  };

  const exportToPDF = (selectedRows) => {
    const doc = new jsPDF();
    const data = selectedRows.length > 0 ? selectedRows : (view === 'puntos' ? puntosData : canjeData);

    const columns = view === 'puntos' ? [
      { header: "Nombre", dataKey: "nombre" },
      { header: "Puntos Positivos", dataKey: "puntosPositivos" },
      { header: "Puntos Negativos", dataKey: "puntosNegativos" },
      { header: "Fecha", dataKey: "fecha" }
    ] : [
      { header: "Nombre", dataKey: "nombre" },
      { header: "Premio", dataKey: "premio" },
      { header: "Descripción del Premio", dataKey: "descripcionPremio" },
      { header: "Puntos", dataKey: "puntos" },
      { header: "Fecha", dataKey: "fecha" }
    ];

    const bodyData = data.map(item => ({
      ...item,
      fecha: item.fecha.toLocaleDateString('es-ES')
    }));

    // Encabezado
    doc.setFontSize(18);
    doc.text('Reporte de Recursos Humanos', 14, 22);

    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`Generado por: ${username}`, 14, 32);
    doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-ES')}`, 14, 38);

    doc.line(14, 40, 196, 40); // Línea divisoria

    // Pie de página
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }

    // Configuración de autoTable
    doc.autoTable({
      head: [columns.map(col => col.header)],
      body: bodyData.map(item => columns.map(col => item[col.dataKey])),
      startY: 50,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [22, 160, 133], textColor: 255 },
      footStyles: { fillColor: [22, 160, 133], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 50 }
    });

    doc.save('report.pdf');
  };

  const exportToExcel = (selectedRows) => {
    const data = selectedRows.length > 0 ? selectedRows : (view === 'puntos' ? puntosData : canjeData);
    const formattedData = data.map(item => ({
      ...item,
      fecha: item.fecha.toLocaleDateString('es-ES')
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, 'report.xlsx');
  };

  const nombreFilterTemplate = (options) => {
    return <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target.value)} placeholder="Buscar por nombre" />;
  };

  const puntosPositivosFilterTemplate = (options) => {
    return <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target.value)} placeholder="Buscar por puntos positivos" />;
  };

  const puntosNegativosFilterTemplate = (options) => {
    return <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target.value)} placeholder="Buscar por puntos negativos" />;
  };

  const dateBodyTemplate = (rowData) => {
    return rowData.fecha.toLocaleDateString('es-ES');
  };

  const renderMenu = () => (
    <div className="menu-container">
      <Button
        label="Puntos de Empleados"
        icon="pi pi-chart-line"
        className="p-button-raised p-button-info"
        onClick={() => setView('puntos')}
      />
      <Button
        label="Canjes de Empleados"
        icon="pi pi-gift"
        className="p-button-raised p-button-success"
        onClick={() => setView('canje')}
      />
    </div>
  );

  const renderPuntosTable = () => (
    <div className="table-container">
      <h2>Puntos de Empleados</h2>
      <Button label="Regresar al Menú" icon="pi pi-arrow-left" className="p-button-raised p-button-secondary" onClick={handleBackToMenu} />
      <Button label="Exportar a PDF" icon="pi pi-file-pdf" className="p-button-raised p-button-danger" onClick={() => exportToPDF(selectedItems)} />
      <Button label="Exportar a Excel" icon="pi pi-file-excel" className="p-button-raised p-button-success" onClick={() => exportToExcel(selectedItems)} />
      {loading ? (
        <p>Cargando datos...</p>
      ) : (
        <DataTable value={puntosData} paginator rows={10} responsiveLayout="scroll" selectionMode="multiple" selection={selectedItems} onSelectionChange={e => setSelectedItems(e.value)} dataKey="id">
          <Column field="nombre" header="Nombre" sortable filter filterElement={nombreFilterTemplate} />
          <Column field="puntosPositivos" header="Puntos Positivos" sortable filter filterElement={puntosPositivosFilterTemplate} />
          <Column field="puntosNegativos" header="Puntos Negativos" sortable filter filterElement={puntosNegativosFilterTemplate} />
          <Column field="fecha" header="Fecha" sortable body={dateBodyTemplate} />
        </DataTable>
      )}
    </div>
  );

  const renderCanjeTable = () => (
    <div className="table-container">
      <h2>Canjes de Empleados</h2>
      <Button label="Regresar al Menú" icon="pi pi-arrow-left" className="p-button-raised p-button-secondary" onClick={handleBackToMenu} />
      <Button label="Exportar a PDF" icon="pi pi-file-pdf" className="p-button-raised p-button-danger" onClick={() => exportToPDF(selectedItems)} />
      <Button label="Exportar a Excel" icon="pi pi-file-excel" className="p-button-raised p-button-success" onClick={() => exportToExcel(selectedItems)} />
      {loading ? (
        <p>Cargando datos...</p>
      ) : (
        <DataTable value={canjeData} paginator rows={10} responsiveLayout="scroll" selectionMode="multiple" selection={selectedItems} onSelectionChange={e => setSelectedItems(e.value)} dataKey="id">
          <Column field="nombre" header="Nombre" sortable filter filterElement={nombreFilterTemplate} />
          <Column field="premio" header="Premio" sortable filter />
          <Column field="descripcionPremio" header="Descripción del Premio" sortable filter />
          <Column field="puntos" header="Puntos" sortable filter />
          <Column field="fecha" header="Fecha" sortable body={dateBodyTemplate} />
        </DataTable>
      )}
    </div>
  );

  return (
    <div className="ReportesRH">
      <h1>Reportes de Recursos Humanos</h1>
      {view === 'menu' ? (
        renderMenu()
      ) : view === 'puntos' ? (
        renderPuntosTable()
      ) : (
        renderCanjeTable()
      )}
    </div>
  );
};

export default ReportesRH;
